// The dashboard's figures, one currency at a time: amounts in different
// currencies are never added together. A port of dashboard_summary.dart.

import { type IsoDate, parts } from '@/core/dates';
import { type Invoice, isDraft, statusOn, totalsOf } from '@/core/invoice';
import { type Currency, currencyOf, Money } from '@/core/money';

export interface MonthTotal {
  year: number;
  /** 1–12. */
  month: number;
  received: Money;
}

export interface DashboardSummary {
  currency: Currency;
  /** The last six months, oldest first; the last is this month. */
  months: MonthTotal[];
  pending: Money;
  pendingCount: number;
  overdue: Money;
  overdueCount: number;
  /** Unsent invoices, in any currency. */
  draftCount: number;
}

const key = (year: number, month: number) => year * 12 + (month - 1);

export function dashboardSummary(invoices: Invoice[], currency: Currency, today: IsoDate): DashboardSummary {
  const now = parts(today);
  const months: MonthTotal[] = [];
  for (let back = 5; back >= 0; back--) {
    const k = key(now.year, now.month) - back;
    months.push({ year: Math.floor(k / 12), month: (k % 12) + 1, received: Money.zero(currency) });
  }
  const first = key(months[0].year, months[0].month);
  let pending = Money.zero(currency);
  let overdue = Money.zero(currency);
  let pendingCount = 0;
  let overdueCount = 0;
  let draftCount = 0;
  for (const invoice of invoices) {
    if (isDraft(invoice)) {
      draftCount++;
      continue;
    }
    if (invoice.currency !== currency.code) continue;
    for (const payment of invoice.payments) {
      const p = parts(payment.date);
      const index = key(p.year, p.month) - first;
      if (index >= 0 && index < months.length) {
        months[index] = { ...months[index], received: months[index].received.plus(new Money(payment.amountMinor, currency)) };
      }
    }
    switch (statusOn(invoice, today)) {
      case 'pending':
      case 'partiallyPaid':
        pending = pending.plus(totalsOf(invoice).balance);
        pendingCount++;
        break;
      case 'overdue':
        overdue = overdue.plus(totalsOf(invoice).balance);
        overdueCount++;
        break;
      default:
        break;
    }
  }
  return { currency, months, pending, pendingCount, overdue, overdueCount, draftCount };
}

export const receivedThisMonth = (s: DashboardSummary) => s.months[s.months.length - 1].received;

/** Change from last month in whole percent; null when last month was zero. */
export function changeFromLastMonth(s: DashboardSummary): number | null {
  const last = s.months[s.months.length - 2].received;
  if (!last.isPositive) return null;
  return Math.round(((receivedThisMonth(s).minor - last.minor) * 100) / last.minor);
}

/** Currencies of sent invoices, most used first; [fallback] when none. */
export function currenciesInUse(invoices: Invoice[], fallback: Currency): Currency[] {
  const counts = new Map<string, number>();
  for (const invoice of invoices) {
    if (!isDraft(invoice)) counts.set(invoice.currency, (counts.get(invoice.currency) ?? 0) + 1);
  }
  if (counts.size === 0) return [fallback];
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([code]) => currencyOf(code));
}

export type Greeting = 'morning' | 'afternoon' | 'evening';

/** Morning before noon, afternoon until 5pm, then evening. */
export function greetingFor(hour: number): Greeting {
  return hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
}
